import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API, clearToken } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { 
  Brain, Dumbbell, ClipboardList, BarChart3, LogOut, 
  Sparkles, ChevronRight, ChevronDown, ChevronUp, CheckCircle2, Play, Sun, Moon, Zap, Flame, Trophy,
  Calendar, Gamepad2, Heart, FileText, Users, Bell, Settings,
  Quote, Target, BookOpen, Plus, TrendingUp, Crown, FlaskConical,
  MessageCircle, User as UserIcon, Info
} from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import NotificationCenter from "../components/NotificationCenter";
import DarkModeToggle from "../components/DarkModeToggle";
import { WelcomeTour, resetWelcomeTour } from "../components/WelcomeTour";
import PushOptIn from "../components/PushOptIn";
import { useSubscription } from "../components/SubscriptionGate";
import { AvatarDisplay } from "../components/SettingsWidgets";
import { ActivityCalendar, ScoreRing, FeatureNavCard } from "../components/DashboardWidgets";
import { GoalsCard, JournalCard, PersonalBestsCard } from "../components/DashboardSections";
import { devWarn } from "../utils/devLogger";
import FeedbackWidget from "../components/FeedbackWidget";
import { FeatureMenuDropdown } from "../components/FeatureMenu";

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [todayQuestionnaire, setTodayQuestionnaire] = useState(null);
  const [todayPlan, setTodayPlan] = useState(null);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [streak, setStreak] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [notifications, setNotifications] = useState({ unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);
  const [heatmap, setHeatmap] = useState([]);
  const [personalBests, setPersonalBests] = useState(null);
  const [dailyQuote, setDailyQuote] = useState(null);
  const [journal, setJournal] = useState([]);
  const [goals, setGoals] = useState([]);
  const [exploreOpen, setExploreOpen] = useState(() => {
    const saved = localStorage.getItem("lumifit_explore_open");
    return saved !== null ? saved === "true" : true;
  });
  const [profile, setProfile] = useState(null);
  const { isPro, tier } = useSubscription();
  const [showTourReplay, setShowTourReplay] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [questRes, planRes, streakRes, recsRes, notifRes, scoreRes, heatRes, bestsRes, quoteRes, journalRes, goalsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/questionnaire/today`),
        axios.get(`${API}/training/today`),
        axios.get(`${API}/streak`),
        axios.get(`${API}/recommendations`).catch(() => ({ data: { recommendations: [] } })),
        axios.get(`${API}/notifications`).catch(() => ({ data: { unread_count: 0 } })),
        axios.get(`${API}/score`).catch(() => ({ data: { scores: { overall: 0, physical: 0, cognitive: 0, consistency: 0, wellness: 0 } } })),
        axios.get(`${API}/activity/heatmap`).catch(() => ({ data: { heatmap: [] } })),
        axios.get(`${API}/personal-bests`).catch(() => ({ data: { bests: {} } })),
        axios.get(`${API}/quote/daily`).catch(() => ({ data: { quote: null } })),
        axios.get(`${API}/journal`).catch(() => ({ data: { entries: [] } })),
        axios.get(`${API}/goals`).catch(() => ({ data: { goals: [] } })),
        axios.get(`${API}/settings`).catch(() => ({ data: { profile: null } })),
      ]);
      setTodayQuestionnaire(questRes.data.response);
      setTodayPlan(planRes.data.plan);
      setCompletedExercises(planRes.data.completed_exercises || []);
      setStreak(streakRes.data);
      setRecommendations(recsRes.data.recommendations || []);
      setNotifications(notifRes.data);
      setScore(scoreRes.data.scores);
      setHeatmap(heatRes.data.heatmap || []);
      setPersonalBests(bestsRes.data.bests);
      setDailyQuote(quoteRes.data.quote);
      setJournal(journalRes.data.entries || []);
      setGoals(goalsRes.data.goals || []);
      setProfile(settingsRes.data.profile);
    } catch (error) {
      devWarn("Dashboard data fetch failed:", error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) { devWarn("Logout:", e?.message); } finally { clearToken(); navigate("/"); }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: t('dashboard.greeting.morning'), icon: Sun };
    if (hour < 18) return { text: t('dashboard.greeting.afternoon'), icon: Sun };
    return { text: t('dashboard.greeting.evening'), icon: Moon };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  const physicalProgress = todayPlan 
    ? (completedExercises.filter(id => todayPlan.physical_exercises.some(e => e.id === id)).length / todayPlan.physical_exercises.length) * 100
    : 0;
  
  const cognitiveProgress = todayPlan
    ? (completedExercises.filter(id => todayPlan.cognitive_exercises.some(e => e.id === id)).length / todayPlan.cognitive_exercises.length) * 100
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="dashboard">
      <WelcomeTour forceShow={showTourReplay} onForceClose={() => setShowTourReplay(false)} />
      <PushOptIn />
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="app-logo-home">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>{t('common.appName')}</span>
            {tier !== null && !isPro && (
              <button onClick={(e) => { e.stopPropagation(); navigate('/subscription'); }} className="ml-1 p-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors" title="Upgrade to Pro" data-testid="header-upgrade-crown">
                <Crown className="w-4 h-4 text-amber-500" strokeWidth={2} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            {streak && streak.current_streak > 0 && (
              <div className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-gradient-to-r from-orange-400/20 to-red-400/20 border border-orange-200 cursor-pointer" onClick={() => navigate('/achievements')} data-testid="header-streak-badge">
                <Flame className="w-4 h-4 text-orange-500" strokeWidth={2} />
                <span className="text-sm font-semibold text-orange-600">{streak.current_streak}</span>
              </div>
            )}
            <div className="hidden sm:flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity px-2 py-1 rounded-lg hover:bg-sky-100/50" onClick={() => navigate('/settings')} data-testid="header-user-profile">
              {profile ? (
                <AvatarDisplay profile={profile} size="w-8 h-8" textSize="text-sm" />
              ) : user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center">
                  <span className="text-sm text-white">{user?.name?.[0]}</span>
                </div>
              )}
              <span className="text-sm text-sky-700">{user?.name}</span>
              {tier === "diamond" && (
                <span className="px-1.5 py-0.5 rounded-md bg-gradient-to-r from-violet-500 to-indigo-500 text-[10px] font-bold text-white" data-testid="diamond-badge">DIAMOND</span>
              )}
              {tier === "pro" && (
                <span className="px-1.5 py-0.5 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 text-[10px] font-bold text-white" data-testid="pro-badge">PRO</span>
              )}
            </div>
            <NotificationCenter />
            <Button variant="ghost" size="icon" onClick={() => navigate('/messages')} className="hidden sm:flex text-sky-600 hover:text-sky-800 hover:bg-sky-100" data-testid="header-messages-btn" title="Messages">
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <div className="hidden sm:block"><FeatureMenuDropdown /></div>
            <Button variant="ghost" size="icon" onClick={() => navigate('/features')} className="sm:hidden text-sky-600 hover:text-sky-800 hover:bg-sky-100 min-w-[44px] min-h-[44px]" data-testid="mobile-features-btn" title="All Features">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" /></svg>
            </Button>
            <div className="hidden sm:block"><DarkModeToggle /></div>
            <div className="hidden sm:block"><LanguageSwitcher /></div>
            <Button variant="ghost" onClick={() => navigate('/profile-card')} className="hidden sm:flex text-sky-600 hover:text-sky-800 hover:bg-sky-100 gap-1.5 px-2 sm:px-3" data-testid="profile-card-btn" title={t("dashboard.shareProfile")}>
              <UserIcon className="w-5 h-5" strokeWidth={1.5} />
              <span className="hidden sm:inline text-xs font-medium">{t("dashboard.profile") || "Profile"}</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="text-sky-600 hover:text-sky-800 hover:bg-sky-100 min-w-[44px] min-h-[44px]" data-testid="settings-btn">
              <Settings className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="hidden sm:flex text-sky-600 hover:text-sky-800 hover:bg-sky-100" data-testid="logout-btn">
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 sm:pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Greeting + Quote */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sky-500 mb-2">
              <GreetingIcon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium">{greeting.text}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
              {t('dashboard.welcome', { name: user?.name?.split(' ')[0] || '' })}
            </h1>
            {dailyQuote && (
              <div className="mt-3 flex items-start gap-2 max-w-xl" data-testid="daily-quote">
                <Quote className="w-4 h-4 text-sky-300 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-sm text-slate-500 italic">"{dailyQuote.text}" <span className="not-italic text-slate-400">— {dailyQuote.author}</span></p>
              </div>
            )}
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            
            {/* Body & Mind Score */}
            <div className="md:col-span-4">
              <div className="glass-card glass-card-hover p-6 cursor-pointer" onClick={() => navigate('/score')} data-testid="body-mind-score-card">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>Body & Mind Score</h3>
                {score ? (
                  <>
                    <div className="flex justify-center mb-4">
                      <div className="relative">
                        <ScoreRing score={score.overall} label="" size={100} color="#0ea5e9" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-2xl font-bold text-sky-900" style={{ fontFamily: 'Outfit' }}>{Math.round(score.overall)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: "Physical", value: score.physical, color: "text-cyan-500" },
                        { label: "Cognitive", value: score.cognitive, color: "text-indigo-500" },
                        { label: "Consistency", value: score.consistency, color: "text-orange-500" },
                        { label: "Wellness", value: score.wellness, color: "text-green-500" },
                      ].map(s => (
                        <div key={s.label} className="text-center">
                          <p className="text-xs text-slate-500">{s.label}</p>
                          <p className={`text-lg font-semibold ${s.color}`} style={{ fontFamily: 'Outfit' }}>{Math.round(s.value)}</p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500 text-center">Complete your first questionnaire to see your score</p>
                )}
              </div>
            </div>

            {/* Today Status */}
            <div className="md:col-span-8">
              <div className="glass-card p-6 md:p-8" data-testid="today-status-card">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>{t('dashboard.todaysStatus')}</h3>
                  <span className="text-sm text-sky-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                </div>
                {todayQuestionnaire ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { label: 'Mood', value: todayQuestionnaire.mood_score },
                      { label: 'Energy', value: todayQuestionnaire.energy_level },
                      { label: 'Focus', value: todayQuestionnaire.focus_level },
                      { label: 'Stress', value: todayQuestionnaire.stress_level },
                      { label: 'Sleep', value: todayQuestionnaire.sleep_quality },
                      { label: 'Motivation', value: todayQuestionnaire.motivation_level }
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white/40 rounded-xl p-4 text-center">
                        <p className="text-xs text-sky-600 mb-1">{stat.label}</p>
                        <p className="text-2xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>{stat.value}<span className="text-sm text-sky-400">/10</span></p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-sky-100 flex items-center justify-center">
                      <ClipboardList className="w-8 h-8 text-sky-400" strokeWidth={1.5} />
                    </div>
                    <p className="text-sky-700 mb-2">{t('dashboard.completeCheckin')}</p>
                    <p className="text-sm text-slate-500 mb-4">{t('dashboard.checkinSubtitle')}</p>
                    <Button onClick={() => navigate('/questionnaire')} className="glow-button" data-testid="complete-checkin-btn">{t('dashboard.startCheckin')}</Button>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions + Activity Calendar */}
            <div className="md:col-span-4 space-y-4">
              <div className="glass-card p-6" data-testid="quick-actions-card">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>{t('dashboard.quickActions')}</h3>
                <div className="space-y-3">
                  <Button onClick={() => navigate('/questionnaire')} className={`w-full justify-start gap-3 ${todayQuestionnaire ? 'btn-secondary-glass' : 'glow-button'}`} data-testid="start-questionnaire-btn">
                    <ClipboardList className="w-5 h-5" strokeWidth={1.5} />
                    <span>{todayQuestionnaire ? t('dashboard.updateCheckin') : t('dashboard.dailyCheckin')}</span>
                    {todayQuestionnaire && <CheckCircle2 className="w-4 h-4 ml-auto text-green-500" />}
                  </Button>
                  <Button onClick={() => navigate('/training')} className="w-full justify-start gap-3 btn-secondary-glass" disabled={!todayQuestionnaire} data-testid="view-training-btn">
                    <Play className="w-5 h-5" strokeWidth={1.5} /><span>{t('dashboard.todaysTraining')}</span><ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                  <Button onClick={() => navigate('/progress')} className="w-full justify-start gap-3 btn-secondary-glass" data-testid="view-progress-btn">
                    <BarChart3 className="w-5 h-5" strokeWidth={1.5} /><span>{t('dashboard.viewProgress')}</span><ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                  <Button onClick={() => navigate('/achievements')} className="w-full justify-start gap-3 btn-secondary-glass" data-testid="view-achievements-btn">
                    <Trophy className="w-5 h-5" strokeWidth={1.5} /><span>{t('dashboard.achievements')}</span><ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                </div>
              </div>
              <div className="glass-card p-4" data-testid="activity-calendar-card">
                <h3 className="text-sm font-medium text-sky-800 mb-2" style={{ fontFamily: 'Outfit' }}>Activity Calendar</h3>
                <div className="overflow-x-auto"><ActivityCalendar data={heatmap} /></div>
              </div>
            </div>

            {/* Feature Grid */}
            <div className="md:col-span-8">
              <div className="glass-card p-6" data-testid="feature-grid-card">
                <button className="w-full flex items-center justify-between mb-0 group" onClick={() => { const next = !exploreOpen; setExploreOpen(next); localStorage.setItem("lumifit_explore_open", String(next)); }} data-testid="explore-toggle-btn">
                  <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>{t('dashboard.exploreFeatures')}</h3>
                  {exploreOpen ? <ChevronUp className="w-5 h-5 text-slate-400 group-hover:text-sky-500 transition-colors" strokeWidth={1.5} /> : <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-sky-500 transition-colors" strokeWidth={1.5} />}
                </button>
                {exploreOpen && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                    {[
                      { icon: Calendar, label: t('dashboard.weeklyCalendar'), path: '/calendar', color: 'text-sky-500' },
                      { icon: Gamepad2, label: t('dashboard.brainGames'), path: '/games', color: 'text-indigo-500' },
                      { icon: Heart, label: t('dashboard.mindfulness'), path: '/mindfulness', color: 'text-pink-500', pro: true },
                      { icon: FileText, label: t('dashboard.weeklyReport'), path: '/report', color: 'text-cyan-500', pro: true },
                      { icon: BookOpen, label: 'Science Blog', path: '/blog', color: 'text-emerald-500' },
                      { icon: TrendingUp, label: "Weekly Digest", path: '/digest', color: 'text-green-500', pro: true },
                      { icon: BookOpen, label: t('dashboard.deepDive'), path: '/deep-dive', color: 'text-violet-500', pro: true },
                      { icon: FlaskConical, label: t('workoutLab.title'), path: '/workout-lab', color: 'text-cyan-500', pro: true },
                      { icon: Crown, label: t('subscription.badge'), path: '/subscription', color: 'text-amber-500' },
                    ].map(item => (
                      <FeatureNavCard key={item.path} item={item} navigate={navigate} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Physical Progress */}
            <div className="md:col-span-6">
              <div className="glass-card glass-card-hover p-6" data-testid="physical-progress-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-400/20 flex items-center justify-center"><Dumbbell className="w-5 h-5 text-cyan-500" strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>{t('dashboard.physicalTraining')}</h3>
                    <p className="text-sm text-slate-500">{t('dashboard.provides50')}</p>
                  </div>
                </div>
                {todayPlan ? (
                  <>
                    <Progress value={physicalProgress} className="h-2 bg-cyan-100" />
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-cyan-600">{completedExercises.filter(id => todayPlan.physical_exercises.some(e => e.id === id)).length} / {todayPlan.physical_exercises.length} {t('dashboard.exercises')}</span>
                      <span className="text-cyan-500 font-medium">{Math.round(physicalProgress)}%</span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">{t('dashboard.generatePlan')}</p>
                )}
              </div>
            </div>

            {/* Cognitive Progress */}
            <div className="md:col-span-6">
              <div className="glass-card glass-card-hover p-6" data-testid="cognitive-progress-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-300/30 flex items-center justify-center"><Brain className="w-5 h-5 text-indigo-500" strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>{t('dashboard.cognitiveTraining')}</h3>
                    <p className="text-sm text-slate-500">{t('dashboard.provides50')}</p>
                  </div>
                </div>
                {todayPlan ? (
                  <>
                    <Progress value={cognitiveProgress} className="h-2 bg-indigo-100" />
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-indigo-600">{completedExercises.filter(id => todayPlan.cognitive_exercises.some(e => e.id === id)).length} / {todayPlan.cognitive_exercises.length} {t('dashboard.exercises')}</span>
                      <span className="text-indigo-500 font-medium">{Math.round(cognitiveProgress)}%</span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">{t('dashboard.generatePlan')}</p>
                )}
              </div>
            </div>

            {/* Personal Bests */}
            <div className="md:col-span-6">
              <PersonalBestsCard personalBests={personalBests} />
            </div>

            {/* Goals */}
            <div className="md:col-span-6">
              <GoalsCard goals={goals} t={t} />
            </div>

            {/* AI Recommendation */}
            {todayPlan && (
              <div className="md:col-span-12">
                <div className="glass-card p-6 border-l-4 border-sky-400" data-testid="ai-recommendation-card">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-sky-400/20 flex items-center justify-center flex-shrink-0"><Zap className="w-5 h-5 text-sky-500" strokeWidth={1.5} /></div>
                    <div>
                      <h4 className="text-sm font-medium text-sky-800 mb-1">{t('dashboard.aiRecommendation')}</h4>
                      <p className="text-slate-600">{todayPlan.ai_recommendation}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Smart Recommendations */}
            {recommendations.length > 0 && (
              <div className="md:col-span-12">
                <div className="space-y-3">
                  {recommendations.slice(0, 2).map((rec) => (
                    <div key={`rec-${rec.title}`}
                      className={`glass-card p-4 border-l-4 cursor-pointer hover:shadow-md transition-all ${rec.priority === 'high' ? 'border-red-400 bg-red-50/30' : ''} ${rec.priority === 'medium' ? 'border-amber-400 bg-amber-50/30' : ''} ${rec.priority === 'low' ? 'border-sky-400' : ''}`}
                      onClick={() => {
                        if (rec.action === 'training') navigate('/training');
                        else if (rec.action === 'meditation') navigate('/mindfulness');
                        else if (rec.action === 'games') navigate('/games');
                        else if (rec.action === 'calendar') navigate('/calendar');
                      }}>
                      <div className="flex items-center gap-3">
                        <Bell className={`w-5 h-5 ${rec.priority === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                        <div>
                          <p className="font-medium text-sky-800">{rec.title}</p>
                          <p className="text-sm text-slate-600">{rec.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Journal */}
            <div className="md:col-span-6">
              <JournalCard journal={journal} t={t} />
            </div>
          </div>
        </div>

        {/* Tour Replay */}
        <div className="flex justify-center mt-8 mb-2">
          <button onClick={() => { resetWelcomeTour(); setShowTourReplay(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sky-400 hover:text-sky-600 hover:bg-sky-100/50 transition-all text-xs"
            data-testid="replay-tour-info-btn" title={t("dashboard.replayTour")}>
            <Info className="w-4 h-4" strokeWidth={1.5} /><span>{t("dashboard.replayTour")}</span>
          </button>
        </div>
      </main>
      <FeedbackWidget />

      {/* Mobile Bottom Navigation */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-sky-100 dark:border-slate-700 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around px-2 py-2">
          {[
            { icon: ClipboardList, label: "Check-in", path: "/questionnaire", testId: "mobile-nav-checkin" },
            { icon: Dumbbell, label: "Train", path: "/training", testId: "mobile-nav-train" },
            { icon: Gamepad2, label: "Games", path: "/games", testId: "mobile-nav-games" },
            { icon: MessageCircle, label: "Messages", path: "/messages", testId: "mobile-nav-messages" },
            { icon: UserIcon, label: "Profile", path: "/settings", testId: "mobile-nav-profile" },
          ].map(item => {
            const NavIcon = item.icon;
            return (
              <button key={item.path} onClick={() => navigate(item.path)} className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg hover:bg-sky-50 dark:hover:bg-slate-800 transition-colors min-w-[56px] min-h-[44px]" data-testid={item.testId}>
                <NavIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
                <span className="text-[10px] text-sky-700 dark:text-sky-300">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Dashboard;
